import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useStreamingQuery } from '@/hooks/useStreamingQuery';
import { EditIssueDialog } from '@/components/EditIssueDialog';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowLeft, BookOpen, CalendarDays, Download, ExternalLink, Hash, Loader2, MoreVertical, Pencil, Trash2, Copy, FileText, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { KIND_MAGAZINE_ISSUE } from '@/lib/pressstrKinds';
import { formatBytes } from '@/lib/blossomUpload';
import { downloadFile } from '@/lib/downloadFile';
import { nip19 } from 'nostr-tools';

interface IssuePageProps {
  pubkey: string;
  identifier: string;
}

export function IssuePage({ pubkey, identifier }: IssuePageProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const issueFilters = useMemo(
    () => [{ kinds: [KIND_MAGAZINE_ISSUE], authors: [pubkey], '#d': [identifier], limit: 1 }],
    [pubkey, identifier],
  );
  const { events, isLoading } = useStreamingQuery(issueFilters, ['issue', pubkey, identifier]);

  const issue: NostrEvent | undefined = events[0];
  const author = useAuthor(pubkey);
  const authorMeta = author.data?.metadata;

  const title = issue?.tags.find(([n]) => n === 'title')?.[1] ?? 'Issue';
  const image = issue?.tags.find(([n]) => n === 'image')?.[1];
  const summary = issue?.tags.find(([n]) => n === 'summary')?.[1];
  const url = issue?.tags.find(([n]) => n === 'url')?.[1];
  const size = issue?.tags.find(([n]) => n === 'size')?.[1];
  const issueNum = issue?.tags.find(([n]) => n === 'issue')?.[1];
  const publishedAt = issue?.tags.find(([n]) => n === 'published_at')?.[1];

  // Parent magazine reference
  const aTag = issue?.tags.find(([n]) => n === 'a')?.[1];
  const parentNaddr = aTag ? (() => {
    const parts = aTag.split(':');
    if (parts.length >= 3) {
      try {
        return nip19.naddrEncode({ kind: parseInt(parts[0]), pubkey: parts[1], identifier: parts[2] });
      } catch { return null; }
    }
    return null;
  })() : null;

  const npub = nip19.npubEncode(pubkey);
  const naddr = issue ? nip19.naddrEncode({ kind: KIND_MAGAZINE_ISSUE, pubkey: issue.pubkey, identifier }) : '';
  const isOwner = user?.pubkey === pubkey;

  const copyNaddr = async () => {
    await navigator.clipboard.writeText(naddr).catch(() => {});
    toast({ title: 'Copied!', description: 'naddr copied to clipboard.' });
  };

  const handleDownload = async () => {
    if (!url || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile(url, `${title.replace(/[/\\:*?"<>|]/g, '_')}.pdf`);
    } catch {
      toast({ title: 'Download failed', description: 'Could not download the file.', variant: 'destructive' });
    } finally {
      setIsDownloading(false);
    }
  };

  const publishDate = publishedAt
    ? new Date(parseInt(publishedAt) * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : undefined;

  useSeoMeta({
    title: `${title} — PressStr`,
    description: summary ?? `Read ${title} on PressStr`,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6 space-y-6 max-w-4xl">
          <Skeleton className="h-8 w-32" />
          <div className="flex flex-col sm:flex-row gap-6">
            <Skeleton className="w-36 sm:w-48 aspect-[2/3] rounded-xl shrink-0 mx-auto sm:mx-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="border-dashed max-w-md w-full">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Issue not found.</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky nav */}
      <div className="border-b sticky top-0 bg-background/95 backdrop-blur z-30">
        <div className="container mx-auto px-4 h-12 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild>
            {parentNaddr
              ? <Link to={`/${parentNaddr}`}><ArrowLeft className="w-4 h-4 mr-1" />Back to Magazine</Link>
              : <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
            }
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={copyNaddr}>
                <Copy className="w-4 h-4 mr-2" />Copy naddr
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" />Edit Issue
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />Delete Issue
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-8">
        {/* Hero section */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
          {/* Cover */}
          <div className="w-44 sm:w-56 aspect-[2/3] rounded-xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shrink-0 mx-auto sm:mx-0 shadow-xl">
            {image ? (
              <img src={image} alt={title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-16 h-16 text-slate-400" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0 space-y-4 text-center sm:text-left">
            {/* Badges */}
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              <Badge className="bg-blue-600 text-white border-0">
                <FileText className="w-3 h-3 mr-1" />PDF
              </Badge>
              {issueNum && (
                <Badge variant="outline" className="font-medium">
                  <Hash className="w-3 h-3 mr-1" />{issueNum}
                </Badge>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{title}</h1>

            {summary && (
              <p className="text-muted-foreground text-lg leading-relaxed break-words">{summary}</p>
            )}

            {issue.content && (
              <p className="text-muted-foreground text-sm break-words whitespace-pre-wrap">{issue.content}</p>
            )}

            {/* Metadata */}
            <div className="flex flex-wrap gap-4 text-sm pt-1 justify-center sm:justify-start">
              {publishDate && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <CalendarDays className="w-4 h-4" />
                  <span>{publishDate}</span>
                </div>
              )}
              {size && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <FileText className="w-4 h-4" />
                  <span>{formatBytes(parseInt(size))}</span>
                </div>
              )}
            </div>

            {/* CTA buttons */}
            {url && (
              <div className="flex flex-wrap gap-3 pt-1 justify-center sm:justify-start">
                <Button asChild size="lg">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-5 h-5 mr-2" />Read Online
                  </a>
                </Button>
                <Button size="lg" variant="outline" onClick={handleDownload} disabled={isDownloading}>
                  {isDownloading
                    ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Downloading…</>
                    : <><Download className="w-5 h-5 mr-2" />Download PDF</>
                  }
                </Button>
              </div>
            )}

            {/* Uploader */}
            <div className="pt-2 border-t flex items-center gap-2 justify-center sm:justify-start">
              <Link to={`/${npub}`} className="shrink-0">
                <Avatar className="w-8 h-8 hover:opacity-80 transition-opacity">
                  <AvatarImage src={authorMeta?.picture} />
                  <AvatarFallback>{authorMeta?.name?.[0] ?? 'P'}</AvatarFallback>
                </Avatar>
              </Link>
              <div className="text-left">
                <p className="text-xs text-muted-foreground">Uploaded by</p>
                <Link to={`/${npub}`} className="text-sm font-medium hover:text-primary transition-colors">
                  {authorMeta?.name ?? npub.slice(0, 20) + '…'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Comments */}
        <section className="space-y-4 pb-10">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Discussion
          </h2>
          <CommentsSection
            root={issue}
            emptyStateMessage="No comments yet"
            emptyStateSubtitle="Be the first to share your thoughts about this issue!"
          />
        </section>
      </div>

      {issue && (
        <>
          <EditIssueDialog open={editOpen} onOpenChange={setEditOpen} issue={issue} onSuccess={() => {}} />
          <ConfirmDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} eventId={issue.id} label="Issue" onSuccess={() => navigate(parentNaddr ? `/${parentNaddr}` : '/')} />
        </>
      )}
    </div>
  );
}
